import { useState } from "react";
import { EyeOff, MousePointerClick, RotateCcw, ShieldCheck, Skull, Smartphone, Sparkles, Tablet, Timer } from "lucide-react";

// Démo jouable des produits VantaPhone : la vraie interface du téléphone ou de la
// tablette, branchée sur un faux serveur (public/demo/demo-shim.js). Tout reste dans
// l'onglet du visiteur et disparaît au rechargement.
const DEVICES = {
  yphone: {
    label: "Yphone",
    Icon: Smartphone,
    src: "/demo/yphone/index.html",
    tips: [
      "Glisse vers le haut pour déverrouiller",
      "Écris à Léa dans Messages : elle te répond",
      "Appelle Mehdi depuis Téléphone",
      "Installe TokTik depuis le VantaStore",
      "Prends une photo, elle arrive dans Photos",
    ],
  },
  tablette: {
    label: "Tablette",
    Icon: Tablet,
    src: "/demo/tablette/index.html",
    tips: [
      "Glisse vers le haut pour déverrouiller",
      "Espace pro : ton équipe LSPD et ta prise de service",
      "Terminal : interventions, avis de recherche, rapports",
      "Messagerie pro : écris à ton équipe",
      "Notes, calculatrice, météo, appareil photo…",
    ],
    illegalTips: [
      "Glisse vers le haut pour déverrouiller",
      "App Groupe : membres, grades et radio du groupe",
      "Classement de la saison et attaques de zones",
      "Coffre, carte des membres et journal",
      "Messagerie pro : parle à ton groupe",
    ],
  },
};

// Tablette : on choisit le côté à essayer, un métier légal (LSPD) ou un groupe illégal.
const SIDES = [
  { id: "legal", label: "Légal", hint: "Police (LSPD)", Icon: ShieldCheck, active: "bg-sky-500 text-white shadow" },
  { id: "illegal", label: "Illégal", hint: "Groupe (Ballas)", Icon: Skull, active: "bg-rose-500 text-white shadow" },
];

const DEMO_FOR = {
  "vantaphone-yphone": ["yphone"],
  "vantaphone-tablette": ["tablette"],
  "vantaphone-pack": ["yphone", "tablette"],
};

export const hasDeviceDemo = (productId) => Boolean(DEMO_FOR[productId]);

export default function DeviceDemo({ productId }) {
  const devices = DEMO_FOR[productId] || [];
  const [device, setDevice] = useState(devices[0]);
  const [run, setRun] = useState(0);
  const [side, setSide] = useState("legal");
  if (!devices.length) return null;
  const current = DEVICES[device];
  const isTablet = device === "tablette";
  const illegal = isTablet && side === "illegal";
  const tips = illegal ? current.illegalTips : current.tips;
  const src = illegal ? `${current.src}?mode=illegal` : current.src;

  return (
    <div className="overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0c0d14]" data-testid="device-demo">
      <div className={`grid gap-0 ${isTablet ? "" : "lg:grid-cols-[minmax(0,1fr)_480px]"}`}>
        <div className="relative p-6 sm:p-8">
          <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-violet-600/20 blur-[90px]" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/65">
              <Sparkles className="h-3.5 w-3.5 text-violet-300" />Démo jouable
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight sm:text-3xl">Essaie-le en direct</h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/55">
              C'est la vraie interface {isTablet ? "de la tablette" : "du Yphone"}, comme en jeu. Clique sur les apps, écris, appelle, installe des apps : tout est simulé.
              {isTablet && " Choisis en haut le côté légal (métier de police) ou illégal (groupe) pour voir les apps de chacun."}
            </p>

            {devices.length > 1 && (
              <div className="mt-5 inline-flex rounded-xl border border-white/[0.08] bg-white/[0.03] p-1" role="tablist">
                {devices.map((id) => {
                  const { label, Icon } = DEVICES[id];
                  const active = id === device;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setDevice(id)}
                      data-testid={`device-demo-tab-${id}`}
                      className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition ${active ? "bg-white text-[#0a0a0f] shadow" : "text-white/60 hover:text-white"}`}
                    >
                      <Icon className="h-4 w-4" />{label}
                    </button>
                  );
                })}
              </div>
            )}

            <ul className={`mt-6 grid gap-2.5 ${isTablet ? "sm:grid-cols-2 lg:grid-cols-3" : ""}`}>
              {tips.map((tip) => (
                <li key={tip} className="flex items-start gap-2.5 text-sm text-white/70">
                  <MousePointerClick className="mt-0.5 h-4 w-4 shrink-0 text-violet-300" />{tip}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-wrap gap-2 text-[11.5px] text-white/50">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] px-2.5 py-1"><Timer className="h-3.5 w-3.5" />Temporaire : recharger la page remet tout à zéro</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] px-2.5 py-1"><EyeOff className="h-3.5 w-3.5" />Privé : toi seul vois ta démo, rien n'est envoyé</span>
            </div>

            <button
              type="button"
              onClick={() => setRun((n) => n + 1)}
              data-testid="device-demo-reset"
              className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2 text-sm text-white/75 transition hover:bg-white/[0.08] hover:text-white"
            >
              <RotateCcw className="h-4 w-4" />Réinitialiser la démo
            </button>
          </div>
        </div>

        <div className={`relative flex flex-col items-center justify-center bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.18),transparent_70%)] ${isTablet ? "border-t border-white/[0.06] px-3 pb-4 pt-4 sm:px-6" : "border-t border-white/[0.06] p-3 lg:border-l lg:border-t-0"}`}>
          {isTablet && (
            <div className="mb-3 inline-flex rounded-2xl border border-white/[0.08] bg-[#0a0a0f]/80 p-1 backdrop-blur" role="tablist" aria-label="Côté de la démo">
              {SIDES.map(({ id, label, hint, Icon, active }) => {
                const on = side === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setSide(id)}
                    data-testid={`device-demo-side-${id}`}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${on ? active : "text-white/60 hover:text-white"}`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                    <span className={`hidden text-xs font-normal sm:inline ${on ? "text-white/80" : "text-white/40"}`}>· {hint}</span>
                  </button>
                );
              })}
            </div>
          )}
          <iframe
            key={`${device}-${side}-${run}`}
            src={src}
            title={`Démo ${current.label}`}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
            data-testid="device-demo-frame"
            className="block w-full border-0 bg-transparent"
            style={isTablet ? { aspectRatio: "1100 / 800", maxWidth: "min(1100px, calc(88vh * 1.375))" } : { height: "min(860px, 88vh)", maxWidth: 460 }}
          />
        </div>
      </div>
    </div>
  );
}
